import React, { useEffect, useState } from 'react';
import {
  Snackbar,
  Alert,
  AlertColor,
  SnackbarOrigin,
  SxProps,
  Theme,
} from '@mui/material';
import { CheckCircle, Error, Warning, Info } from '@mui/icons-material';

// Type definitions
export interface CustomSnackBarProps {
  // Core properties
  open?: boolean;
  message: string;
  severity?: AlertColor;

  // Auto-show and timing
  autoShow?: boolean;
  autoHideDuration?: number | null;

  // Positioning
  anchorOrigin?: SnackbarOrigin;

  // Styling
  sx?: SxProps<Theme>;
  alertSx?: SxProps<Theme>;

  // Behavior
  showCloseButton?: boolean;
  preventDuplicates?: boolean;

  // Callbacks
  onClose?: (event?: React.SyntheticEvent | Event, reason?: string) => void;
  onOpen?: () => void;

  // Custom content
  icon?: React.ReactNode;
  action?: React.ReactNode;

  // Advanced options
  variant?: 'filled' | 'outlined' | 'standard';
  disableClickaway?: boolean;
}

// Default icons for each severity
const defaultIcons: Record<AlertColor, React.ReactNode> = {
  success: <CheckCircle />,
  error: <Error />,
  warning: <Warning />,
  info: <Info />,
};

// Default styles for each severity
const defaultStyles: Record<AlertColor, any> = {
  success: {
    backgroundColor: '#d4edda',
    color: '#155724',
  },
  error: {
    backgroundColor: '#f8d7da',
    color: '#721c24',
  },
  warning: {
    backgroundColor: '#fff3cd',
    color: '#856404',
  },
  info: {
    backgroundColor: '#cce7ff',
    color: '#004085',
  },
};

const CustomSnackBar: React.FC<CustomSnackBarProps> = ({
  open: controlledOpen,
  message,
  severity = 'info',
  autoShow = false,
  autoHideDuration = 6000,
  anchorOrigin = { vertical: 'top', horizontal: 'right' },
  sx,
  alertSx,
  showCloseButton = true,
  preventDuplicates = false,
  onClose,
  onOpen,
  icon,
  action,
  variant = 'filled',
  disableClickaway = false,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [lastMessage, setLastMessage] = useState('');

  // Determine if we should show the notification
  const isControlled = controlledOpen !== undefined;
  const shouldShow = isControlled ? controlledOpen : internalOpen;

  // ✅ When onClose is provided, disable auto-hiding by setting autoHideDuration to null
  const effectiveAutoHideDuration = onClose ? null : autoHideDuration;

  useEffect(() => {
    if (autoShow && message) {
      // Prevent duplicates if enabled
      if (preventDuplicates && message === lastMessage) {
        return;
      }

      setLastMessage(message);
      setInternalOpen(true);
      onOpen?.();
    }
  }, [message, autoShow, preventDuplicates, lastMessage, onOpen]);

  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    // Don't close on clickaway if disabled
    if (disableClickaway && reason === 'clickaway') {
      return;
    }

    // ✅ When onClose is provided, only close on manual actions (not timeout)
    if (onClose && reason === 'timeout') {
      return;
    }

    if (isControlled) {
      onClose?.(event, reason);
    } else {
      setInternalOpen(false);
      onClose?.(event, reason);
    }
  };

  // Don't render if no message
  if (!message) return null;

  // Determine icon to use
  const finalIcon = icon !== undefined ? icon : defaultIcons[severity];

  // Combine styles
  const finalAlertSx: any = {
    fontSize: '1.5rem',
    display: 'flex',
    letterSpacing: '1px',
    alignItems: 'center',
    '& .MuiAlert-icon': {
      fontSize: '2.25rem',
      marginRight: '8px',
      alignSelf: 'center',
    },
    '& .MuiAlert-message': {
      display: 'flex',
      alignItems: 'center',
      flex: 1,
    },
    '& .MuiSvgIcon-root': {
      fontSize: '1.5rem',
    },
    '& .MuiAlert-action': {
      marginTop: 0,
      alignItems: 'center',
      display: 'flex',
    },
    ...defaultStyles[severity],
    ...alertSx,
  };

  return (
    <Snackbar
      open={shouldShow}
      autoHideDuration={effectiveAutoHideDuration}
      onClose={handleClose}
      anchorOrigin={anchorOrigin}
      sx={sx}
    >
      <Alert
        onClose={showCloseButton ? handleClose : undefined}
        severity={severity}
        variant={variant}
        icon={finalIcon}
        action={action}
        sx={finalAlertSx}
      >
        {message}
      </Alert>
    </Snackbar>
  );
};

export default CustomSnackBar;

// Usage Examples:

/*
// Basic usage - will auto-hide after 6 seconds
<CustomSnackBar
  message="This will auto-hide"
  severity="success"
  autoShow={true}
/>

// With onClose prop - will NOT auto-hide, only manual close allowed
<CustomSnackBar
  message="This will stay until manually closed"
  severity="warning"
  autoShow={true}
  onClose={(event, reason) => {
    console.log('Manually closed:', reason);
    // Handle manual close logic here
  }}
/>

// Controlled usage with onClose - manual close only
const [showNotif, setShowNotif] = useState(false);
<CustomSnackBar
  open={showNotif}
  message="Custom notification - manual close only"
  severity="error"
  onClose={() => setShowNotif(false)}
/>

// Force auto-hide even with onClose by explicitly setting autoHideDuration
<CustomSnackBar
  message="This will auto-hide even with onClose"
  severity="info"
  autoShow={true}
  autoHideDuration={3000}  // This will be ignored when onClose is present
  onClose={(event, reason) => {
    if (reason !== 'timeout') {
      console.log('Manually closed');
    }
  }}
/>

// Critical error that should not auto-hide
<CustomSnackBar
  message="Critical error occurred! Please take action."
  severity="error"
  autoShow={true}
  onClose={(event, reason) => {
    // Only allow manual closing
    handleCriticalErrorClose();
  }}
/>
*/
